import { ModuleWithProviders, NgModule } from '@angular/core';
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { ApolloClientOptions, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
// Need to ignore the import, because there are no module types, and the ones from @types/extract-files are for v11 yet.
// @ts-expect-error old ts-ignore
import extractFiles from 'extract-files/extractFiles.mjs';
// @ts-expect-error old ts-ignore
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { environment } from '../environments/environment';
import { onError } from '@apollo/client/link/error';
import { Router } from '@angular/router';
import Rollbar from 'rollbar';
import { RollbarService } from './error-handlers/rollbar';
import { UserStorageService } from './services/user/user.storage.service';
import { setContext } from '@apollo/client/link/context';
import {CustomerServiceImpl} from "./services/customer/customer.service.impl";

const uri = environment.graphQlUri;

export function createApollo(httpLink: HttpLink, router: Router, rollBar: Rollbar, userStorageService: UserStorageService): ApolloClientOptions<any> {

  const appUserContext = setContext(async (operation, prevContext) => {
    // if header is already set, use it
    if (prevContext?.['headers']?.['X-door-vision-app-user'] != undefined) {
      return {
        ...prevContext,
        headers: {
          ...prevContext['headers'],
          'apollo-require-preflight': 'true',
        }
      };
    }

    // wait until userId is set in local storage and is validated against backend
    const appUserId = await userStorageService.waitForUserIdIsValidated();
    if (!appUserId) {
      return {
        ...prevContext,
        headers: {
          ...prevContext['headers'],
          'apollo-require-preflight': 'true',
        }
      };
    }

    // add userId to header
    return {
      ...prevContext,
      headers: {
        'X-door-vision-app-user': appUserId || '',
        'X-door-vision-customer-key': CustomerServiceImpl.GetCustomerKey(),
        'X-door-vision-version': environment.version,
        'apollo-require-preflight': 'true',
        ...prevContext['headers'],
      }
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ extensions, message, locations, path }) => {
          console.warn(`[GraphQL error]: Operation: ${operation.operationName}, Message: ${message}, Path: ${path}`);
          rollBar.error(`[GraphQL error]: Operation: ${operation.operationName}, Message: ${message}, Path: ${path}`, extensions, locations);
        }
      );
      if (!operation.getContext()['ignoreDefaultErrorHandling']) {
        router.navigateByUrl('error').then();
      }
    }


    if (networkError) {
      console.error(`[Network error]: Operation: ${operation.operationName}`, networkError);
      rollBar.error(`[Network error]: Operation: ${operation.operationName}`, networkError);
      router.navigateByUrl('error').then();
    }
  });

  const mainLink = httpLink.create({ uri, extractFiles: (body) => extractFiles(body, isExtractableFile) });

  return {
    // 2. Set httpLink with an anonymous function to adapt extract-files
    link: ApolloLink.from([appUserContext, errorLink, mainLink]),
    cache: new InMemoryCache(),
  };
}

const providers = [
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [HttpLink, Router, RollbarService, UserStorageService],
  },
];

@NgModule({
  imports: [ApolloModule],
  providers,
  exports: [ApolloModule]
})
export class GraphQLModule {
  public static forRoot(): ModuleWithProviders<GraphQLModule> {
    return {
      ngModule: GraphQLModule,
      providers
    };
  }
}
